import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FiSave, FiUpload, FiTrash2, FiImage, FiMail, FiPhone, FiMapPin, FiLinkedin, FiTwitter, FiFacebook, FiX } from 'react-icons/fi';
import { api } from '../../utils/api';
import { triggerRefresh, REFRESH_EVENTS } from '../../utils/refreshEvents';
import './AdminSettings.css';

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState({
    siteName: '',
    logo: '',
    footer: {
      description: '',
      phone: '',
      email: '',
      location: '',
      socialLinks: {
        linkedin: '',
        twitter: '',
        facebook: ''
      }
    },
    contact: {
      phone: '',
      email: '',
      location: ''
    }
  });
  const [logoPreview, setLogoPreview] = useState('');
  const [logoFile, setLogoFile] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.getSettings();
      setSettings(res.data);
      setLogoPreview(res.data.logo || '');
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('footer.')) {
      const field = name.split('.')[1];
      if (field === 'socialLinks') {
        const socialField = name.split('.')[2];
        setSettings(prev => ({
          ...prev,
          footer: {
            ...prev.footer,
            socialLinks: {
              ...prev.footer.socialLinks,
              [socialField]: value
            }
          }
        }));
      } else {
        setSettings(prev => ({
          ...prev,
          footer: {
            ...prev.footer,
            [field]: value
          }
        }));
      }
    } else if (name.startsWith('contact.')) {
      const field = name.split('.')[1];
      setSettings(prev => ({
        ...prev,
        contact: {
          ...prev.contact,
          [field]: value
        }
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Le fichier est trop volumineux. Taille maximale: 5MB');
        return;
      }
      
      if (!file.type.match(/^image\/(png|jpg|jpeg|svg|webp)$/)) {
        alert('Format de fichier non supporté. Formats acceptés: PNG, JPG, JPEG, SVG, WEBP');
        return;
      }

      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      alert('Veuillez sélectionner un fichier');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      
      const res = await api.uploadLogo(formData);
      setSettings(prev => ({ ...prev, logo: res.data.logo }));
      setLogoPreview(res.data.logo);
      setLogoFile(null);
      alert('Logo uploadé avec succès !');
      // Déclencher le rafraîchissement sur les pages publiques
      triggerRefresh(REFRESH_EVENTS.SETTINGS);
    } catch (error) {
      alert('Erreur lors de l\'upload: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer le logo ?')) {
      return;
    }

    try {
      await api.deleteLogo();
      setSettings(prev => ({ ...prev, logo: '' }));
      setLogoPreview('');
      setLogoFile(null);
      alert('Logo supprimé avec succès !');
      // Déclencher le rafraîchissement sur les pages publiques
      triggerRefresh(REFRESH_EVENTS.SETTINGS);
    } catch (error) {
      alert('Erreur lors de la suppression: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await api.updateSettings(settings);
      alert('Paramètres mis à jour avec succès !');
      // Déclencher le rafraîchissement sur les pages publiques
      triggerRefresh(REFRESH_EVENTS.SETTINGS);
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-admin">
        <div className="loading-spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="admin-settings">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2>Paramètres du site</h2>
      </motion.div>

      <form onSubmit={handleSubmit} className="settings-form">
        {/* Section Logo */}
        <motion.div
          className="settings-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <h3>
            <FiImage className="section-icon" />
            Logo du site
          </h3>
          
          <div className="logo-upload-section">
            <div className="logo-preview-wrapper">
              {logoPreview ? (
                <div className="logo-preview-container">
                  <img src={logoPreview} alt="Logo preview" className="logo-preview" />
                  <button
                    type="button"
                    onClick={handleLogoDelete}
                    className="logo-delete-btn"
                    title="Supprimer le logo"
                  >
                    <FiX />
                  </button>
                </div>
              ) : (
                <div className="logo-placeholder">
                  <FiImage className="placeholder-icon" />
                  <span>Aucun logo</span>
                </div>
              )}
            </div>
            
            <div className="logo-upload-controls">
              <label className="file-input-label">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                  onChange={handleLogoSelect}
                  className="file-input"
                />
                <span className="file-input-button">
                  <FiUpload />
                  Choisir un fichier
                </span>
              </label>
              
              {logoFile && (
                <motion.button
                  type="button"
                  onClick={handleLogoUpload}
                  className="btn btn-primary"
                  disabled={uploading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {uploading ? 'Upload en cours...' : 'Uploader le logo'}
                </motion.button>
              )}
            </div>
            
            <p className="logo-hint">
              Formats acceptés: PNG, JPG, JPEG, SVG, WEBP. Taille maximale: 5MB
            </p>
          </div>
        </motion.div>

        {/* Section Informations générales */}
        <motion.div
          className="settings-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h3>Informations générales</h3>
          
          <div className="form-group">
            <label>Nom du site *</label>
            <input
              type="text"
              name="siteName"
              value={settings.siteName}
              onChange={handleChange}
              required
              placeholder="VirtualHand"
            />
          </div>
        </motion.div>

        {/* Section Footer */}
        <motion.div
          className="settings-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <h3>Informations du Footer</h3>
          
          <div className="form-group">
            <label>Description *</label>
            <textarea
              name="footer.description"
              value={settings.footer.description}
              onChange={handleChange}
              required
              rows="3"
              placeholder="Description du site..."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <FiPhone className="label-icon" />
                Téléphone *
              </label>
              <input
                type="tel"
                name="footer.phone"
                value={settings.footer.phone}
                onChange={handleChange}
                required
                placeholder="07 82 65 74 37"
              />
            </div>

            <div className="form-group">
              <label>
                <FiMail className="label-icon" />
                Email *
              </label>
              <input
                type="email"
                name="footer.email"
                value={settings.footer.email}
                onChange={handleChange}
                required
                placeholder="sou.virtualhand@gmail.com"
              />
            </div>

            <div className="form-group">
              <label>
                <FiMapPin className="label-icon" />
                Localisation *
              </label>
              <input
                type="text"
                name="footer.location"
                value={settings.footer.location}
                onChange={handleChange}
                required
                placeholder="France"
              />
            </div>
          </div>

          <div className="social-links-section">
            <h4>Réseaux sociaux</h4>
            <div className="form-row">
              <div className="form-group">
                <label>
                  <FiLinkedin className="label-icon" />
                  LinkedIn
                </label>
                <input
                  type="url"
                  name="footer.socialLinks.linkedin"
                  value={settings.footer.socialLinks.linkedin}
                  onChange={handleChange}
                  placeholder="https://linkedin.com/company/..."
                />
              </div>

              <div className="form-group">
                <label>
                  <FiTwitter className="label-icon" />
                  Twitter
                </label>
                <input
                  type="url"
                  name="footer.socialLinks.twitter"
                  value={settings.footer.socialLinks.twitter}
                  onChange={handleChange}
                  placeholder="https://twitter.com/..."
                />
              </div>

              <div className="form-group">
                <label>
                  <FiFacebook className="label-icon" />
                  Facebook
                </label>
                <input
                  type="url"
                  name="footer.socialLinks.facebook"
                  value={settings.footer.socialLinks.facebook}
                  onChange={handleChange}
                  placeholder="https://facebook.com/..."
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section Contact */}
        <motion.div
          className="settings-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3>Informations de contact</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>
                <FiPhone className="label-icon" />
                Téléphone *
              </label>
              <input
                type="tel"
                name="contact.phone"
                value={settings.contact.phone}
                onChange={handleChange}
                required
                placeholder="07 82 65 74 37"
              />
            </div>

            <div className="form-group">
              <label>
                <FiMail className="label-icon" />
                Email *
              </label>
              <input
                type="email"
                name="contact.email"
                value={settings.contact.email}
                onChange={handleChange}
                required
                placeholder="sou.virtualhand@gmail.com"
              />
            </div>

            <div className="form-group">
              <label>
                <FiMapPin className="label-icon" />
                Localisation *
              </label>
              <input
                type="text"
                name="contact.location"
                value={settings.contact.location}
                onChange={handleChange}
                required
                placeholder="France"
              />
            </div>
          </div>
        </motion.div>

        <motion.div
          className="form-actions"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <motion.button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiSave />
            <span>{saving ? 'Enregistrement...' : 'Enregistrer les modifications'}</span>
          </motion.button>
        </motion.div>
      </form>
    </div>
  );
};

export default AdminSettings;



